import { memo, useCallback } from 'react';

import settings from '~/settings';
import { BATTLE_TYPES } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { isClanBattleType } from '~/helpers/ladder';
import { useAppDispatch, useAppSelector } from '~/store';
import { membersCurrentSeasonDataSelector } from '~/store/selectors/membersSelectors';
import { clanRatingsSelector } from '~/store/selectors/ratingsSelectors';
import { actionsClanProfile, fetchMembers } from '~/Actions/ActionClanProfile';

import BattleSelector from '~/UIKit/BattleSelector/BattleSelector';
import LadderSeasonsDropdown from '~/UIKit/LadderSeasonsDropdown/LadderSeasonsDropdown';
import LadderTeamsDropdown from '~/UIKit/LadderTeamsDropdown/LadderTeamsDropdown';
import Ratings from '~/UIKit/Ratings/Ratings';

import styles from './ClanRatings.module.scss';

import type { IBattleType } from '~/types/declaration';

interface ClanProfileRatingsProps {
  clanId: number;
  isOwnClanBase?: boolean;
  dropdownPosition?: 'top' | 'bottom' | 'left';
}

const ClanProfileRatings = ({ clanId, isOwnClanBase = false, dropdownPosition = 'top' }: ClanProfileRatingsProps) => {
  const dispatch = useAppDispatch();

  const { membersBattleTypes, membersTeamNumber, clanMembersStatistics, statistics } = useAppSelector((state) =>
    clanRatingsSelector(state, clanId),
  );
  const { currentSeason, currentSeasonType } = useAppSelector(membersCurrentSeasonDataSelector);

  const { isMissingStatistics = false, isHiddenStatistics = false } = clanMembersStatistics ?? {};
  const isSeasonMenuAvailable = currentSeason !== null && isClanBattleType(membersBattleTypes);

  const handleBattleTypeChange = useCallback(
    (battleType: IBattleType) => {
      if (isOwnClanBase) {
        let battleTypeForDwh: string = battleType;
        if (battleType === BATTLE_TYPES.COOPERATIVE) {
          battleTypeForDwh = 'coop';
        }
        dwhExport.push(DWH_EVENTS.MEMBERS.SELECT_BATTLE_TYPE, {
          battle_type: battleTypeForDwh,
        });
      }

      dispatch(actionsClanProfile.changeClanBattleType(clanId, battleType));

      void dispatch(fetchMembers(clanId));
    },
    [clanId, dispatch, isOwnClanBase],
  );

  const handleSeasonChange = useCallback(
    (season: number) => {
      if (isOwnClanBase) {
        dwhExport.push(DWH_EVENTS.MEMBERS.SELECT_SEASON, {
          season: season,
        });
      }

      dispatch(actionsClanProfile.setClanProfileCurrentSeason(season));

      void dispatch(fetchMembers(clanId));
    },
    [clanId, dispatch, isOwnClanBase],
  );

  const handleTeamNumberChange = useCallback(
    (teamNumber: number) => {
      if (isOwnClanBase) {
        dwhExport.push(DWH_EVENTS.MEMBERS.SELECT_TEAM_NUMBER, {
          teamNumber: teamNumber,
        });
      }

      dispatch(actionsClanProfile.changeClanTeamNumber(clanId, teamNumber));

      void dispatch(fetchMembers(clanId));
    },
    [clanId, dispatch, isOwnClanBase],
  );

  return (
    <>
      <div className={styles.battleSelectorWrap}>
        <BattleSelector
          key="battle-selector"
          items={settings.battleTypes}
          selected={membersBattleTypes}
          onBattleTypeChange={handleBattleTypeChange}
          dropdownPosition={dropdownPosition}
        />
      </div>

      {isSeasonMenuAvailable && (
        <>
          {!!currentSeasonType && (
            <div className={styles.seasonMenu}>
              <LadderSeasonsDropdown
                selectedSeasonId={currentSeason}
                selectedSeasonType={currentSeasonType}
                onSeasonChange={handleSeasonChange}
                dropdownPosition={dropdownPosition}
              />
            </div>
          )}
          <div className={styles.seasonMenu}>
            <LadderTeamsDropdown
              selectedTeamNumber={membersTeamNumber}
              season={currentSeason}
              statistics={statistics}
              onTeamNumberChange={handleTeamNumberChange}
            />
          </div>
        </>
      )}

      <Ratings
        clanId={clanId}
        currentSeason={currentSeason}
        isHiddenStatistics={isHiddenStatistics}
        membersError={isMissingStatistics}
        statistics={statistics}
      />
    </>
  );
};

export default memo(ClanProfileRatings);
